import React, {
  ChangeEvent,
  forwardRef,
  InputHTMLAttributes,
  useEffect,
  useState,
} from "react";
import {
  StyledLabel,
  StyledFieldContainer,
  StyledIconContainer,
  StyledCloseContainer,
  StyledInputContainer,
  StyledInput,
  StyledStartIconContainer,
} from "./Input.styled";
import { FontWeightEnum, IconSizeEnum } from "../../../types/AppTypes";
import { IconManager } from "../IconManager/IconManager";
import { ReactComponent as CloseIcon } from "../../../icons/close.svg";
import { BodyTyp } from "../../Typography/Typography";
import { theme } from "../../../constants/StyledComponents";
import { trimSpaces } from "../../../utils/string.utils";

export enum InputSizes {
  REGULAR = "regular",
  MEDIUM = "medium",
  SMALL = "small",
}

export enum InputBorderEnum {
  DEFAULT = "default",
  BOTTOM = "bottom",
}
export interface InputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "onChange"
> {
  label?: string;
  error?: string;
  endIcon?: React.ReactNode;
  startIcon?: React.ReactNode;
  allowClear?: boolean;
  inputSize?: InputSizes;
  onClear?: CallableFunction;
  count?: number;
  onChange?: (value: string) => void;
  borderVariant?: InputBorderEnum;
  autoTrim?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      disabled,
      label,
      error,
      onChange,
      placeholder,
      endIcon,
      startIcon,
      readOnly,
      allowClear,
      onClear,
      type,
      inputSize = InputSizes.REGULAR,
      borderVariant = InputBorderEnum.DEFAULT,
      count,
      value,
      autoTrim = true,
      onBlur: propsOnBlur,
      ...props
    },
    ref,
  ) => {
    const [isActive, setIsActive] = useState<boolean>(false);

    const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
      const text = e.target.value;

      if (text !== "" || placeholder) {
        setIsActive(true);
      } else {
        setIsActive(false);
      }

      if (onChange) {
        onChange(e.target.value);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (autoTrim && onChange && type !== "password") {
        const trimmedValue = trimSpaces(e.target.value);
        if (trimmedValue !== e.target.value) {
          onChange(trimmedValue);
        }
      }
      if (propsOnBlur) {
        propsOnBlur(e);
      }
    };

    const clear = () => {
      setIsActive(false);

      if (onChange) {
        onChange("");
      }

      if (onClear) {
        onClear();
      }
    };

    useEffect(() => {
      setIsActive(value !== "" && value !== undefined && value !== null);
    }, [value]);

    return (
      <StyledFieldContainer $hasError={!!error}>
        <StyledInputContainer
          disabled={disabled}
          $readOnly={readOnly}
          $hasError={!!error}
          $inputSize={inputSize}
          $borderVariant={borderVariant}
        >
          <StyledStartIconContainer>{startIcon}</StyledStartIconContainer>
          <StyledInput
            ref={ref}
            type={type}
            onChange={handleTextChange}
            onBlur={handleBlur}
            disabled={disabled}
            value={value ?? ""}
            placeholder={placeholder}
            readOnly={readOnly}
            $hasEndIcon={!!endIcon}
            $hasStartIcon={!!startIcon}
            $allowClear={allowClear}
            $hasLabel={!!label}
            $hasCount={!!count}
            {...props}
          />
          <StyledLabel
            $hasError={!!error}
            $hasEndIcon={!!endIcon}
            $hasStartIcon={!!startIcon}
            $allowClear={allowClear && !!value}
            $hasCount={!!count}
            $inputSize={inputSize}
            $isActive={isActive}
            $hasPlaceholder={!!placeholder}
          >
            <BodyTyp
              variant="2"
              fontWeight={FontWeightEnum.REGULAR}
              color={error ? theme.colors.red_1 : theme.colors.grey_2}
            >
              {label}
            </BodyTyp>
          </StyledLabel>
          <StyledIconContainer $disabled={disabled}>
            {count && (
              <BodyTyp
                variant="2"
                fontWeight={FontWeightEnum.SEMIBOLD}
                color={theme.colors.purple}
              >{`+${count}`}</BodyTyp>
            )}
            {allowClear && !disabled && (
              <StyledCloseContainer $visible={!!value}>
                <IconManager size={IconSizeEnum.SMALL} onClick={clear}>
                  <CloseIcon />
                </IconManager>
              </StyledCloseContainer>
            )}
            {endIcon}
          </StyledIconContainer>
        </StyledInputContainer>
      </StyledFieldContainer>
    );
  },
);

export default Input;
